import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { analyticsAPI } from "../api";
import { MessageSquare, Search, ChevronLeft, ChevronRight, Smile, Frown, Meh, Star, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const SENTIMENT_ICONS = {
  positive: { icon: Smile, color: "text-green-500", bg: "bg-green-50" },
  negative: { icon: Frown, color: "text-red-500", bg: "bg-red-50" },
  neutral: { icon: Meh, color: "text-gray-500", bg: "bg-gray-50" },
};

function MiniStars({ score }) {
  if (!score) return <span className="text-xs text-gray-300">—</span>;
  return (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${i <= Math.round(score) ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
        />
      ))}
    </div>
  );
}

export default function ConversationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFilter = searchParams.get("customer_id");
  const customerNameFilter = searchParams.get("customer_name");
  const topicFilter = searchParams.get("topic");
  const improvementFilter = searchParams.get("improvement_category");
  const sentimentParam = searchParams.get("sentiment");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sentimentFilter, setSentimentFilter] = useState(sentimentParam || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const fetchIdRef = useRef(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchConversations = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const params = { page, limit: 20, days: 365 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (sentimentFilter !== "all") params.sentiment = sentimentFilter;
      if (search) params.search = search;
      if (customerIdFilter) params.customer_id = customerIdFilter;
      if (topicFilter) params.topic = topicFilter;
      if (improvementFilter) params.improvement_category = improvementFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await analyticsAPI.conversations(params);
      // Ignore stale responses
      if (fetchId !== fetchIdRef.current) return;
      const data = res.data;
      setConversations(data?.items || []);
      setTotalPages(data?.total_pages || 1);
      setTotal(data?.total || 0);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error("Conversations fetch error:", err);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [page, statusFilter, sentimentFilter, search, customerIdFilter, topicFilter, improvementFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return (
    <div className="space-y-6">
      {/* Sticky header + filter */}
      <div className="sticky top-[-1rem] lg:top-[-2rem] z-20 -mt-4 lg:-mt-8 -mx-4 lg:-mx-8 px-4 lg:px-8 pt-4 lg:pt-8 pb-4 bg-sky-50/90 backdrop-blur-md">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-gray-200/50 pb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Hội thoại</h3>
            <p className="text-gray-500 text-sm mt-1">
              Quản lý và phân tích hội thoại Zalo OA
              {total > 0 && <span className="ml-2 text-gray-400">({total} cuộc)</span>}
            </p>
            {customerIdFilter && (
              <div className="mt-2 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit">
                <span className="text-amber-700">Đang lọc theo khách hàng: <strong>{customerNameFilter || `#${customerIdFilter}`}</strong></span>
                <button onClick={() => navigate("/conversations")} className="text-amber-500 hover:text-amber-700 font-medium ml-1">✕ Xóa lọc</button>
              </div>
            )}
            {topicFilter && (
              <div className="mt-2 flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 w-fit">
                <span className="text-blue-700">📋 Đang lọc theo chủ đề: <strong>{topicFilter}</strong></span>
                <button onClick={() => navigate("/conversations")} className="text-blue-500 hover:text-blue-700 font-medium ml-1">✕ Xóa lọc</button>
              </div>
            )}
            {improvementFilter && (
              <div className="mt-2 flex items-center gap-2 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 w-fit">
                <span className="text-red-700">⚠️ Đang lọc vấn đề: <strong>{improvementFilter}</strong></span>
                <button onClick={() => navigate("/conversations")} className="text-red-500 hover:text-red-700 font-medium ml-1">✕ Xóa lọc</button>
              </div>
            )}
          </div>

          {/* Pagination Top */}
          {totalPages > 1 && (
            <div className="flex items-center space-x-1.5 bg-white backdrop-blur rounded-xl p-1 shadow-sm border border-gray-200/60">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1.5 hover:bg-gray-100 text-gray-600 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Trang trước"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-sm font-medium text-blue-900 px-3 py-1 bg-blue-50/50 rounded-lg border border-blue-100/50">
                Trang {page} <span className="text-gray-400 mx-1">/</span> {totalPages}
              </div>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1.5 hover:bg-gray-100 text-gray-600 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Trang sau"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="card py-3 sm:py-4">
          <div className="flex flex-col gap-3">
            {/* Row 1: search + status + sentiment */}
            <div className="flex flex-col md:flex-row gap-2.5 sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm kiếm tên KH, chủ đề, tóm tắt..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="input-field pl-10 text-sm"
                />
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="input-field flex-1 md:flex-initial md:w-auto text-sm"
                >
                  <option value="all">Tất cả trạng thái</option>
                  <option value="open">Đang mở</option>
                  <option value="resolved">Đã xử lý</option>
                </select>
                <select
                  value={sentimentFilter}
                  onChange={(e) => { setSentimentFilter(e.target.value); setPage(1); }}
                  className="input-field flex-1 md:flex-initial md:w-auto text-sm"
                >
                  <option value="all">Tất cả cảm xúc</option>
                  <option value="positive">Tích cực</option>
                  <option value="neutral">Trung lập</option>
                  <option value="negative">Tiêu cực</option>
                </select>
              </div>
            </div>
            {/* Row 2: date range */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <span className="text-gray-500 whitespace-nowrap">Từ ngày</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="input-field flex-1 sm:flex-initial sm:w-auto text-xs sm:text-sm py-1.5"
              />
              <span className="text-gray-500 whitespace-nowrap">đến ngày</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="input-field flex-1 sm:flex-initial sm:w-auto text-xs sm:text-sm py-1.5"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                  className="text-gray-400 hover:text-gray-600 underline whitespace-nowrap ml-auto sm:ml-0"
                >
                  Xóa ngày
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.length === 0 ? (
            <div className="card text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Không tìm thấy hội thoại nào</p>
            </div>
          ) : (
            conversations.map((conv, i) => {
              const sentimentInfo = SENTIMENT_ICONS[conv.sentiment_label] || SENTIMENT_ICONS.neutral;
              const SentIcon = sentimentInfo.icon;
              return (
                <motion.div
                  key={conv.id || i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className="card hover:shadow-md cursor-pointer transition-all py-3 sm:py-4"
                  onClick={() => navigate(`/conversations/${conv.id}`)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${sentimentInfo.bg}`}>
                        <SentIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${sentimentInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate text-sm sm:text-base">{conv.customer_name || "Khách hàng"}</p>
                          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${conv.status === "resolved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                            }`}>
                            {conv.status === "resolved" ? "Đã đóng" : "Đang mở"}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 truncate mt-0.5">{conv.topic || "Khác"}</p>
                        {conv.summary && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{conv.summary}</p>
                        )}
                        {/* Mobile AI Score Pill */}
                        {conv.has_evaluation && conv.satisfaction_score && (
                          <div className="flex sm:hidden items-center gap-1 mt-1 text-[11px] text-amber-600 font-medium">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <span>{conv.satisfaction_score.toFixed(1)}/5</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* AI Scores (Desktop / Tablet) */}
                    <div className="hidden sm:flex items-center space-x-4 mx-2 lg:mx-4 flex-shrink-0">
                      {conv.has_evaluation ? (
                        <>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">Hài lòng</p>
                            <MiniStars score={conv.satisfaction_score} />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">Thái độ</p>
                            <MiniStars score={conv.agent_attitude_score} />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-400 mb-0.5">Chất lượng</p>
                            <MiniStars score={conv.resolution_quality_score} />
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-300 italic">Chưa đánh giá</span>
                      )}
                    </div>

                    <div className="text-right ml-2 sm:ml-4 flex-shrink-0">
                      <p className="text-xs text-gray-400 whitespace-nowrap">
                        {conv.started_at ? new Date(conv.started_at).toLocaleDateString("vi-VN") : ""}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 sm:mt-1 whitespace-nowrap">{conv.message_count || 0} tin nhắn</p>
                      <ExternalLink className="w-3 h-3 text-gray-300 mt-1 ml-auto" />
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      )}


    </div>
  );
}
